import axios from 'axios';
import { useQuery } from 'react-query';
import { checkoutsApi } from '../../../../api/checkouts';
import { ICheckout } from '../../../../api/checkouts/types/checkout.type';

const { checkLink } = checkoutsApi;

interface IUseCheckoutLinkGateInput {
	checkout: ICheckout | undefined;
	hash: string | undefined;
	/**
	 * Whether the queries that produce `checkout` are still in flight. Without
	 * it the hook cannot tell "no checkout yet" from "this content has no
	 * checkout", and would either flash the form or hang on the skeleton.
	 */
	isCheckoutResolving?: boolean;
}

interface IUseCheckoutLinkGateResult {
	isBlocked: boolean;
	isLoading: boolean;
	/**
	 * The link carries a hash and we still don't know whether it is valid.
	 * Callers must NOT render the checkout form while this is true — an
	 * invalid link would otherwise show the whole form before redirecting.
	 */
	isResolving: boolean;
	hash: string | undefined;
	checkoutId: number | undefined;
}

/**
 * Gates the checkout form behind a single-use link check.
 *
 * - When the checkout is in `UNIQUE` mode, a `?hash=<...>` query param is
 *   required and validated against `GET /checkouts/:id/links/:hash`.
 * - When `mode` is missing from the loaded checkout payload (older backends),
 *   we still run the check whenever a `hash` is present — same effective
 *   gating, just slightly more permissive (a UNIQUE checkout without a hash
 *   would not be blocked). Remove the fallback once `mode` is reliably
 *   exposed by `/checkouts/contents/:id` and `/checkouts/plans/:id`.
 * - When the checkout is `OPEN` (default) and no hash is present, this is a
 *   no-op.
 */
export const useCheckoutLinkGate = ({
	checkout,
	hash,
	isCheckoutResolving = false
}: IUseCheckoutLinkGateInput): IUseCheckoutLinkGateResult => {
	const checkoutId = checkout?.id;

	const isUniqueMode = checkout?.mode === 'UNIQUE';
	const isUnknownMode = checkout && checkout.mode === undefined;

	// UNIQUE without a hash: block immediately, no need to call the API.
	const missingHashOnUnique = isUniqueMode && !hash;

	// Run the GET check when we know it's UNIQUE + have hash, OR when the
	// backend hasn't told us the mode and a hash was provided.
	const shouldCheck =
		!!checkoutId && !!hash && (isUniqueMode || (isUnknownMode && !!hash));

	const { data, isLoading, isSuccess, isError, error } = useQuery(
		[checkLink.key, { checkoutId, hash }],
		checkLink.fetch,
		{
			enabled: shouldCheck,
			retry: false,
			refetchOnWindowFocus: false
		}
	);

	// `isLoading` alone is not enough: a query that was just enabled reports
	// `idle` for a render before it switches to `loading`, and that single
	// render is what used to flash the form.
	const isCheckSettled = isSuccess || isError;

	// Only hash-carrying links are held back — an open checkout keeps
	// rendering as soon as its own data arrives.
	const isResolving =
		!!hash && (isCheckoutResolving || (!!shouldCheck && !isCheckSettled));

	if (missingHashOnUnique) {
		return {
			isBlocked: true,
			isLoading: false,
			isResolving: false,
			hash,
			checkoutId
		};
	}

	if (!shouldCheck) {
		return {
			isBlocked: false,
			isLoading: false,
			isResolving,
			hash,
			checkoutId
		};
	}

	if (isLoading) {
		return {
			isBlocked: false,
			isLoading: true,
			isResolving,
			hash,
			checkoutId
		};
	}

	// 404 from the backend: link is invalid or already used → block.
	if (isError) {
		const status = axios.isAxiosError(error)
			? error.response?.status
			: undefined;

		if (status === 404) {
			return {
				isBlocked: true,
				isLoading: false,
				isResolving: false,
				hash,
				checkoutId
			};
		}

		// Other errors (network, 5xx) are transient — let the user proceed
		// rather than locking them out. The post-success PATCH will still
		// fail loudly on the backend if the link really is invalid.
		return {
			isBlocked: false,
			isLoading: false,
			isResolving: false,
			hash,
			checkoutId
		};
	}

	if (!data?.hash) {
		return {
			isBlocked: true,
			isLoading: false,
			isResolving: false,
			hash,
			checkoutId
		};
	}

	return {
		isBlocked: false,
		isLoading: false,
		isResolving,
		hash,
		checkoutId
	};
};
